import React, { useState, useMemo } from 'react';
import './Roadmap.css';

const roadmapData = [
  {
    category: "General",
    color: "#5af356",
    events: [
      { title: "Startup Initiation", start: "Sep 2021", end: "Mar 2022" },
      { title: "Private Limited Registration", start: "Mar 2022", end: "Mar 2022" },
      { title: "Team Building", start: "Mar 2023", end: "Aug 2023" },
      { title: "Pilot Launch", start: "Jan 2025", end: "Jun 2025" },
      { title: "Launch Process - Final Checks", start: "Apr 2025", end: "Jun 2025" },
      { title: "Product Launch", start: "July 2025", end: "Dec 2026" },
    ],
  },
  {
    category: "Development",
    color: "#33B5FF",
    events: [
      { title: "Market Study", start: "Mar 2022", end: "Mar 2023" },
      { title: "PoS UI Design", start: "Jun 2023", end: "Apr 2024" },
      { title: "PoS App", start: "Sep 2023", end: "Sep 2024" },
      { title: "PoS App Beta", start: "Sep 2024", end: "Sep 2024" },
      { title: "Customer App", start: "Sep 2024", end: "Dec 2024" },
      { title: "Loading Product Details", start: "Oct 2024", end: "Dec 2024" },
      { title: "Feedbacks & Optimization", start: "Feb 2025", end: "Dec 2026" },
    ],
  },
  {
    category: "Marketing",
    color: "#8D33FF",
    events: [
      { title: "Investment Campaign", start: "Oct 2024", end: "Mar 2025" },
      { title: "Sales Team Building", start: "Mar 2025", end: "Dec 2026" },
    ],
  },
  {
    category: "Infrastructure",
    color: "#DA33FF",
    events: [
      { title: "Barcode Scanner", start: "Sep 2023", end: "Mar 2024" },
      { title: "Barcode Scanner Imported", start: "Mar 2024", end: "Mar 2024" },
      { title: "Scaling the Scope", start: "Sep 2025", end: "Dec 2026" },
      { title: "Build Own Cloud Infrastructure", start: "Jan 2026", end: "Dec 2026" },
    ],
  },

];


// Helper function to calculate months difference
const calculateMonths = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return (
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth())
  );
};

const generateBlocks = (timelineStart, timelineEnd, categoryEvents, categoryColor) => {
  const totalMonths = calculateMonths(timelineStart, timelineEnd);
  const eventBlocks = Array(totalMonths).fill({ color: "#C0C0C0", overlap: 0 });

  categoryEvents.forEach((event) => {
    const eventStart = Math.max(0, calculateMonths(timelineStart, event.start));
    const eventEnd = Math.min(totalMonths - 1, calculateMonths(timelineStart, event.end));

    for (let i = eventStart; i <= eventEnd; i++) {
      eventBlocks[i] = {
        color: categoryColor,
        overlap: eventBlocks[i].overlap + 1,
      };
    }
  });

  return eventBlocks;
};


const darkenColor = (color) => {
  const amount = -30;
  let usePound = false;

  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }

  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00ff) + amount;
  let b = (num & 0x0000ff) + amount;

  r = r < 0 ? 0 : r > 255 ? 255 : r;
  g = g < 0 ? 0 : g > 255 ? 255 : g;
  b = b < 0 ? 0 : b > 255 ? 255 : b;

  return (
    (usePound ? "#" : "") +
    ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
  );
};




const Roadmap = () => {
  const timelineStart = "Sep 2021";
  const timelineEnd = "Dec 2026";
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipCategoryColor, setTooltipCategoryColor] = useState("");
  const [activeEvents, setActiveEvents] = useState({});
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Calculate current position based on current date
  const currentMonth = useMemo(() => {
    const now = new Date();
    return calculateMonths(timelineStart, `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`);
  }, []);

  

  const handleMouseEnter = (event, x, y, color) => {
    setHoveredEvent(event);
    setTooltipPosition({ x, y });
    setTooltipCategoryColor(color);
    setTooltipVisible(true);
  };

  const handleTooltipClose = () => {
    setTooltipVisible(false);
  };

  const toggleEventActive = (eventTitle) => {
    setActiveEvents((prev) => ({
      ...prev,
      [eventTitle]: !prev[eventTitle],
    }));
  };

  return (
    <div className="roadmap-container">
      <div className="roadmap">
        <div className="categories-container">

          {/* "We Are Here" Pointer */}
          <div
            className="current-position-indicator"
            style={{
              top: `calc(${currentMonth} * 45px + 50px)`, // Adjust to match the current block + 50px to  cover the category headings
              left: `1px`, // Fine-tuned for better alignment
            }}
          >
            <span role="img" aria-label="pointing-right" className="indicator-icon">
              👉
            </span>
            <span className="indicator-text">We Are Here</span>
          </div>


          {/* Categories */}
          {roadmapData.map((category, categoryIndex) => {
            const blocks = generateBlocks(timelineStart, timelineEnd, category.events, category.color);

            return (              
              
              <div key={categoryIndex} className="category-column">
                {/* Category Heading Container */}
                <div className="category-heading-container">
                  <div className="category-label">{category.category}</div>
                </div>
                

                <div className="category-blocks">
                  {blocks.map((block, blockIndex) => (
                    <div
                      key={blockIndex}
                      className="timeline-block"
                      style={{
                        backgroundColor: block.overlap > 1 ? darkenColor(block.color) : block.color,
                      }}
                    />
                  ))}
                </div>

                {category.events.map((event, eventIndex) => {
                  const startMonth = calculateMonths(timelineStart, event.start);
                  const endMonth = calculateMonths(timelineStart, event.end);

                  return (
                    <React.Fragment key={eventIndex}>
                      {/* Start Indicator with Title */}
                      <div
                        className={`event-point start-indicator ${activeEvents[event.title] ? 'active' : ''}`}
                        style={{
                          top: `${startMonth * 45 + 20}px`,
                          backgroundColor: category.color,
                        }}
                        onClick={(e) => {
                          handleMouseEnter(event, e.clientX, e.clientY, category.color);
                          toggleEventActive(event.title);
                        }}
                      >
                        <span className="event-title">
                          {event.title} - Start
                        </span>
                      </div>

                      {/* End Indicator */}
                      <div
                        className={`event-point end-indicator ${activeEvents[`${event.title}-end`] ? 'active' : ''}`}
                        style={{
                          top: `${endMonth * 45 + 25}px`,
                          backgroundColor: category.color,
                        }}
                        onClick={(e) => {
                          handleMouseEnter(event, e.clientX, e.clientY, category.color);
                          toggleEventActive(`${event.title}-end`);
                        }}
                      >
                        <span className="event-title">
                          {event.title} - End
                        </span>
                      </div>

                    {/* Event Tooltip */}
                    {tooltipVisible && hoveredEvent && hoveredEvent.title === event.title && (
                        <div
                          className="tooltip"
                          style={{
                            top: `${tooltipPosition.y}px`,
                            left: `${tooltipPosition.x}px`,
                            backgroundColor: tooltipCategoryColor,
                          }}
                        >
                          <strong>{hoveredEvent.title} - {hoveredEvent.start === event.start ? 'Start' : 'End'}</strong>
                          <p>
                            {event.start} - {event.end}
                          </p>
                          <button className="close-label" onClick={handleTooltipClose}>X</button>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default Roadmap;